import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { ListToolbar } from "../../components/ListToolbar";
import { Modal } from "../../components/Modal";
import { Tooltip } from "../../components/Tooltip";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  categorizeBankMovement,
  getBankMovementCategories,
  getBankMovements,
  createSupplierFromBankMovement,
  matchBankMovementSupplier,
  matchBankMovementClient,
  createClientFromBankMovement,
  resolveBankMovementEntities,
  type BankMovement,
  type BankMovementCategory,
  type BankMovementEntityResolution,
  type BankMovementEntityResult,
  type BankMovementFilters,
} from "../../movimentos/movimentosApi";
import { getBankAccounts, type BankAccount } from "../../bancos/bancosApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";

// Linhas por página. Com a sincronização automática o histórico cresce sozinho, e trazer tudo a
// cada abertura do ecrã deixou de fazer sentido.
const PAGE_SIZE = 50;

// O botão do assistente é fixo no canto inferior direito (48px de lado, a 24px do fundo). Sem esta
// folga, o "Seguinte" da paginação fica por baixo dele quando se chega ao fim da lista.
const FLOATING_BUTTON_CLEARANCE = 88;

// Verde de sucesso: index.css não garante uma classe text-* verde fora dos badges,
// por isso a cor de valores positivos vai inline (fallback se a var não existir).
const SUCCESS_COLOR = "var(--color-success, #16a34a)";

// Badge de categoria — só pares de classes que existem no index.css pré-compilado.
const CATEGORY_BADGE_CLASS: Record<string, string> = {
  recebimentos: "bg-green-500/10 text-green-700",
  custos_bancarios: "bg-yellow-500/10 text-yellow-700",
  transferencias_internas: "bg-primary/10 text-primary",
  pagamentos_fornecedores: "bg-muted text-secondary-text",
  salarios: "bg-muted text-secondary-text",
  impostos: "bg-muted text-secondary-text",
  outros: "bg-muted text-secondary-text",
};

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// O que um clique arrastou. Sem isto, associar o NOS numa linha e ver as outras quatro mudarem
// parecia magia; com isto parece o que é: um clique a valer por cinco.
const describeEntityResult = (
  result: BankMovementEntityResult,
  entity: "fornecedor" | "cliente",
): string | null => {
  const parts: string[] = [];
  if (result.alsoAssociated > 0) {
    parts.push(
      `${result.alsoAssociated} outro(s) movimento(s) com o mesmo descritivo ficaram com o mesmo ${entity}`,
    );
  }
  if (result.categorized > 0) {
    parts.push(`${result.categorized} ficaram com categoria`);
  }
  if (result.autoReconciled > 0) {
    parts.push(
      `${result.autoReconciled} ficaram reconciliados sozinhos, por a fatura ser certa`,
    );
  }
  return parts.length > 0 ? `${parts.join("; ")}.` : null;
};

// Depois de categorizar à mão: o que a escolha ensinou aos movimentos iguais.
const describeCategorized = (result: BankMovementEntityResult): string | null =>
  result.categorized > 0
    ? `${result.categorized} outro(s) movimento(s) ficaram com categoria sozinhos, a partir da sua escolha.`
    : null;

const describeResolution = (result: BankMovementEntityResolution): string | null => {
  const parts: string[] = [];
  if (result.suppliersLinked > 0) {
    parts.push(`${result.suppliersLinked} movimento(s) ficaram com fornecedor`);
  }
  if (result.clientsLinked > 0) {
    parts.push(`${result.clientsLinked} entrada(s) ficaram com cliente`);
  }
  if (result.categoriesApplied > 0) {
    parts.push(`${result.categoriesApplied} ficaram com categoria`);
  }
  if (result.autoReconciled > 0) {
    parts.push(`${result.autoReconciled} ficaram reconciliados sozinhos`);
  }
  return parts.length > 0 ? `Ao abrir: ${parts.join(", ")}.` : null;
};

// De onde veio o fornecedor. "Aprendido" é o estado que interessa ao utilizador ver crescer: é o
// que já não gasta IA e nunca mais falha.
const SUPPLIER_SOURCE_META: Record<string, { label: string; title: string }> = {
  memory: { label: "Aprendido", title: "Este descritivo já foi confirmado antes — reconhecido sem IA." },
  ai: { label: "IA", title: "Reconhecido pela IA a partir do descritivo do extrato." },
  name: { label: "Nome parecido", title: "Semelhança de nomes, sem IA — por isso é só uma proposta." },
  manual: { label: "Manual", title: "Escolhido por si." },
};

/**
 * Explicação por extenso de como aquele fornecedor ali foi parar. Sem isto o utilizador vê "IA ·
 * 90%" e não tem como saber o que a IA leu nem porque é que uns são associados e outros só
 * propostos — que é a diferença entre confiar no automatismo e desconfiar dele.
 */
function supplierTooltip(m: BankMovement): string {
  const parts: string[] = [];
  const meta = m.supplierMatchSource ? SUPPLIER_SOURCE_META[m.supplierMatchSource] : undefined;
  if (meta) parts.push(meta.title);
  if (m.supplierMatchReason) parts.push(`O que levou lá: ${m.supplierMatchReason}.`);
  if (m.supplierMatchScore != null && m.supplierMatchSource !== "manual") {
    const percent = Math.round(m.supplierMatchScore * 100);
    parts.push(
      m.supplierId != null
        ? `Confiança de ${percent}% — alta o suficiente para associar sem perguntar.`
        : `Confiança de ${percent}% — não chega para associar sozinho, por isso fica como proposta à sua espera.`,
    );
  }
  if (m.supplierNameHint) {
    parts.push(
      `O descritivo nomeia «${m.supplierNameHint}», que não existe na lista de fornecedores. ` +
        "Registe-o aqui, ou associe o movimento a um fornecedor já existente.",
    );
  } else {
    parts.push(
      m.supplierId != null
        ? "Não é preciso fazer nada. Clique no nome para alterar."
        : "Confirme para fixar, ou escolha outro fornecedor.",
    );
  }

  return parts.join(" ");
}

function SupplierSourceBadge({
  source,
  score,
  tooltip,
}: {
  source: string | null;
  score: number | null;
  tooltip: string;
}) {
  if (!source) return null;
  const meta = SUPPLIER_SOURCE_META[source];
  if (!meta) return null;
  const percent = score != null ? ` · ${Math.round(score * 100)}%` : "";
  return (
    <Tooltip text={tooltip}>
      <span
        className="inline-flex items-center px-2 py-0.5 bg-muted text-secondary-text cursor-help"
        style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius)" }}
      >
        {meta.label}
        {percent}
      </span>
    </Tooltip>
  );
}

function CategoryBadge({
  code,
  name,
}: {
  code: string | null;
  name: string | null;
}) {
  if (!code || !name) return <span className="text-secondary-text">—</span>;
  const cls = CATEGORY_BADGE_CLASS[code] ?? "bg-muted text-secondary-text";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 ${cls}`}
      style={{
        fontSize: "var(--text-xs)",
        borderRadius: "var(--radius)",
        fontWeight: "var(--font-weight-medium)",
      }}
    >
      {name}
    </span>
  );
}

export function MovimentosBancariosView() {
  const [movements, setMovements] = useState<BankMovement[]>([]);
  const [categories, setCategories] = useState<BankMovementCategory[]>([]);

  // Filtros (server-side). A categoria é o único classificador — custos bancários e
  // transferências internas são categorias, filtram-se aqui (sem checkboxes redundantes).
  const [categoryId, setCategoryId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [direction, setDirection] = useState<"" | "inflow" | "outflow">("");
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInfo, setPageInfo] = useState({
    totalCount: 0,
    totalPages: 0,
    totalInflow: 0,
    totalOutflow: 0,
  });
  // A pesquisa passou a correr no servidor; esperar por uma pausa evita um pedido por tecla.
  const [debouncedFilter, setDebouncedFilter] = useState("");
  // Texto da pesquisa; corre no servidor sobre descrição e conta.
  const [filter, setFilter] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // O que o automatismo fez pelo utilizador, ao abrir ou depois de um clique. Só aparece quando
  // foi mais do que zero; o silêncio é o normal.
  const [notice, setNotice] = useState<string | null>(null);
  // Sobe quando algo mudou fora da lista (o automatismo ao abrir), para a recarregar sem mexer
  // nos filtros.
  const [refreshTick, setRefreshTick] = useState(0);



  // Modal recategorizar (por movimento).
  const [categorizeTarget, setCategorizeTarget] = useState<BankMovement | null>(null);
  const [categorizeChoice, setCategorizeChoice] = useState("");
  const [isCategorizing, setIsCategorizing] = useState(false);
  const [categorizeError, setCategorizeError] = useState<string | null>(null);



  // Fornecedores: lista partilhada de opções para o seletor.
  const { options: supplierLookupOptions, loading: suppliersLoading } =
    useLookupOptions("suppliers");
  const [supplierTarget, setSupplierTarget] = useState<BankMovement | null>(null);
  const [supplierChoice, setSupplierChoice] = useState("");
  const [supplierError, setSupplierError] = useState<string | null>(null);
  // Nome a registar quando o fornecedor ainda não existe. Sem isto, um movimento cujo descritivo
  // não nomeia ninguém obrigava a sair daqui, ir a Fornecedores, criar, e voltar.
  const [newSupplierName, setNewSupplierName] = useState("");
  const [isSavingSupplier, setIsSavingSupplier] = useState(false);

  // Clientes: o par do fornecedor para as entradas. Só à mão, por agora.
  const { options: clientLookupOptions, loading: clientsLoading } =
    useLookupOptions("clients");
  const [clientTarget, setClientTarget] = useState<BankMovement | null>(null);
  const [clientChoice, setClientChoice] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [newClientName, setNewClientName] = useState("");
  const [isSavingClient, setIsSavingClient] = useState(false);

  // Mudar de filtro volta à primeira página. O reset é feito em render (padrão de derived state),
  // não num useEffect: pelo efeito, a lista chegava a pedir a página antiga com os filtros novos
  // e só depois a página 1 — dois pedidos por cada mudança de filtro.
  const filterSignature = [categoryId, from, to, bankAccountId, direction, debouncedFilter].join("|");
  const previousFilterSignature = useRef(filterSignature);
  if (previousFilterSignature.current !== filterSignature) {
    previousFilterSignature.current = filterSignature;
    if (pageNumber !== 1) setPageNumber(1);
  }

  const filters: BankMovementFilters = useMemo(
    () => ({
      categoryId: categoryId ? Number.parseInt(categoryId, 10) : null,
      from: from || null,
      to: to || null,
      bankAccountId: bankAccountId ? Number.parseInt(bankAccountId, 10) : null,
      direction: direction === "" ? null : direction,
      search: debouncedFilter.trim() === "" ? null : debouncedFilter.trim(),
      pageNumber,
      pageSize: PAGE_SIZE,
    }),
    [categoryId, from, to, bankAccountId, direction, debouncedFilter, pageNumber],
  );

  // Cada pedido leva um número: a resposta de um pedido anterior, se chegar depois, não escreve
  // por cima da página que o utilizador já pediu a seguir.
  const loadSequence = useRef(0);

  const load = useCallback(
    async (silent = false) => {
      const sequence = ++loadSequence.current;
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const page = await getBankMovements(filters);
        if (sequence !== loadSequence.current) return;
        setMovements(page.items);
        setPageInfo({
          totalCount: page.totalCount,
          totalPages: page.totalPages,
          totalInflow: page.totalInflow,
          totalOutflow: page.totalOutflow,
        });
      } catch (err) {
        if (sequence !== loadSequence.current) return;
        setError(errorMessage(err, "Não foi possível carregar os movimentos."));
      } finally {
        if (!silent && sequence === loadSequence.current) setIsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  useEffect(() => {
    // Ao abrir, o que já se sabe aplica-se ao que está pendente: um fornecedor registado ontem na
    // ficha de fornecedores, ou pela importação de faturas, apanha aqui os movimentos que o
    // nomeavam, sem esperar pela próxima sincronização. Se falhar, a lista vale na mesma.
    let cancelled = false;
    resolveBankMovementEntities()
      .then((result) => {
        if (cancelled) return;
        const text = describeResolution(result);
        if (text) {
          setNotice(text);
          setRefreshTick((tick) => tick + 1);
        }
      })
      .catch(() => {
        // Sem faixa; a próxima sincronização volta a tentar.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filter), 350);
    return () => clearTimeout(timer);
  }, [filter]);


  useEffect(() => {
    getBankMovementCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    // As contas servem só para o filtro: sem elas a lista continua a funcionar tal como antes.
    getBankAccounts()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  const supplierSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      supplierLookupOptions.map((option) => ({
        ...option,
        value: String(option.value),
      })),
    [supplierLookupOptions],
  );

  const clientSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      clientLookupOptions.map((option) => ({
        ...option,
        value: String(option.value),
      })),
    [clientLookupOptions],
  );

  // A lista é a página que o servidor mandou, já filtrada e já procurada. Filtrar outra vez aqui
  // só esconderia linhas desta página e daria a entender que não havia mais nenhuma.
  const shown = movements;







  // --- Recategorizar por movimento -----------------------------------------
  const openCategorize = (m: BankMovement) => {
    setCategorizeTarget(m);
    setCategorizeChoice(m.categoryId != null ? String(m.categoryId) : "");
    setCategorizeError(null);
  };

  const handleCategorize = async () => {
    if (!categorizeTarget || isCategorizing) return;
    const chosen = Number.parseInt(categorizeChoice, 10);
    if (!Number.isFinite(chosen) || chosen <= 0) {
      setCategorizeError("Selecione uma categoria.");
      return;
    }
    setIsCategorizing(true);
    setCategorizeError(null);
    try {
      const result = await categorizeBankMovement(categorizeTarget.id, chosen);
      setNotice(describeCategorized(result));
      setCategorizeTarget(null);
      await load(true);
    } catch (err) {
      setCategorizeError(errorMessage(err, "Não foi possível categorizar o movimento."));
    } finally {
      setIsCategorizing(false);
    }
  };


  // --- Fornecedor ----------------------------------------------------------
  const openSupplier = (m: BankMovement) => {
    setSupplierTarget(m);
    // Pré-seleciona o que já lá está, ou a proposta — confirmar passa a ser um clique.
    setSupplierChoice(String(m.supplierId ?? m.suggestedSupplierId ?? ""));
    // E pré-escreve o nome que o descritivo dá, quando dá: quase sempre é esse o fornecedor a
    // registar, e ninguém o quer copiar à mão.
    setNewSupplierName(m.supplierNameHint ?? m.counterpartyName ?? "");
    setSupplierError(null);
  };

  const createSupplierWithName = async (m: BankMovement, name: string) => {
    const trimmed = name.trim();
    if (trimmed === "") return;

    setIsSavingSupplier(true);
    setSupplierError(null);
    try {
      // O servidor reaproveita um fornecedor já existente com o mesmo nome, por isso escrever um
      // nome repetido associa ao que existe em vez de criar um duplicado.
      const result = await createSupplierFromBankMovement(m.id, trimmed);
      setNotice(describeEntityResult(result, "fornecedor"));
      await load(true);
      setSupplierTarget(null);
    } catch (err) {
      setSupplierError(errorMessage(err, "Não foi possível registar o fornecedor."));
    } finally {
      setIsSavingSupplier(false);
    }
  };

  // --- Cliente (entradas) --------------------------------------------------
  const openClient = (m: BankMovement) => {
    setClientTarget(m);
    setClientChoice(String(m.clientId ?? ""));
    setNewClientName("");
    setClientError(null);
  };

  const saveClient = async (m: BankMovement, clientId: number | null) => {
    setIsSavingClient(true);
    setClientError(null);
    try {
      const result = await matchBankMovementClient(m.id, clientId);
      setNotice(describeEntityResult(result, "cliente"));
      await load(true);
      setClientTarget(null);
    } catch (err) {
      setClientError(errorMessage(err, "Não foi possível guardar o cliente."));
    } finally {
      setIsSavingClient(false);
    }
  };

  const createClientWithName = async (m: BankMovement, name: string) => {
    const trimmed = name.trim();
    if (trimmed === "") return;

    setIsSavingClient(true);
    setClientError(null);
    try {
      // Como no fornecedor: um nome que já exista associa ao cliente existente, não duplica.
      const result = await createClientFromBankMovement(m.id, trimmed);
      setNotice(describeEntityResult(result, "cliente"));
      await load(true);
      setClientTarget(null);
    } catch (err) {
      setClientError(errorMessage(err, "Não foi possível registar o cliente."));
    } finally {
      setIsSavingClient(false);
    }
  };

  const saveSupplier = async (m: BankMovement, supplierId: number | null) => {
    setIsSavingSupplier(true);
    setSupplierError(null);
    try {
      const result = await matchBankMovementSupplier(m.id, supplierId);
      setNotice(describeEntityResult(result, "fornecedor"));
      setSupplierTarget(null);
      await load(true);
    } catch (err) {
      setSupplierError(errorMessage(err, "Não foi possível guardar o fornecedor."));
    } finally {
      setIsSavingSupplier(false);
    }
  };

  const createSupplierFromHint = async (m: BankMovement) => {
    if (!m.supplierNameHint || isSavingSupplier) return;
    setIsSavingSupplier(true);
    setError(null);
    try {
      // `createSupplierFromBankMovement` já invalida a lista partilhada de fornecedores,
      // e a invalidação avisa este hook — pedir `reload()` aqui só duplicava o pedido.
      const result = await createSupplierFromBankMovement(m.id, m.supplierNameHint);
      setNotice(describeEntityResult(result, "fornecedor"));
      await load(true);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível registar o fornecedor."));
    } finally {
      setIsSavingSupplier(false);
    }
  };



  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Movimentos bancários"
        subtitle="Os movimentos chegam do banco sozinhos. Reveja a categoria e associe a entidade de cada um; com a entidade posta, a fatura certa liga-se sozinha."
      />

      {/* A live region fica sempre montada (só o texto entra e sai): montá-la já com texto
          falha o anúncio em alguns leitores de ecrã. */}
      <div aria-live="polite">
        {notice && (
          <div
            className="border border-border bg-muted px-4 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {notice}
          </div>
        )}
      </div>

      <div
        className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
        style={{ paddingBottom: FLOATING_BUTTON_CLEARANCE }}
      >
        {/* Toolbar + ações */}
        <ListToolbar
          filter={filter}
          onFilterChange={setFilter}
          placeholder="Procurar por descrição, entidade ou conta…"
          summary={
            <span className="text-secondary-text text-sm">
              {pageInfo.totalCount} movimento(s)
            </span>
          }
        />

        {/* Filtros */}
        <div className="flex items-center gap-3 flex-wrap">
          {accounts.length > 0 && (
            <select
              value={bankAccountId}
              onChange={(event) => setBankAccountId(event.target.value)}
              className={inputClass}
              aria-label="Filtrar por conta"
            >
              <option value="">Todas as contas</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={direction}
            onChange={(event) =>
              setDirection(event.target.value as "" | "inflow" | "outflow")
            }
            className={inputClass}
            aria-label="Filtrar por tipo de movimento"
          >
            <option value="">Entradas e saídas</option>
            <option value="inflow">Só entradas</option>
            <option value="outflow">Só saídas</option>
          </select>
          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className={inputClass}
            aria-label="Filtrar por categoria"
          >
            <option value="">Todas as categorias</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-secondary-text">
            De
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className={inputClass}
              aria-label="Data inicial"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary-text">
            Até
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className={inputClass}
              aria-label="Data final"
            />
          </label>
        </div>



        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {/* Lista */}
        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 1000 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3" style={{ width: 110 }}>Data</th>
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3 text-right" style={{ width: 140 }}>Valor</th>
                <th className="px-4 py-3" style={{ width: 200 }}>Categoria</th>
                {/* "Entidade" e não "Fornecedor": numa saída é o fornecedor a quem se pagou, numa
                    entrada é o cliente que pagou. É a mesma coluna, com a natureza a seguir o sinal. */}
                <th className="px-4 py-3" style={{ width: 230 }}>Entidade</th>
                <th className="px-4 py-3" style={{ width: 130 }}>Conta</th>
                <th className="px-4 py-3" style={{ width: 240 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                    A carregar…
                  </td>
                </tr>
              ) : shown.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                    {filter !== "" || categoryId || from || to || bankAccountId || direction
                      ? "Sem movimentos para o filtro."
                      : "Sem movimentos. Importe um extrato ou adicione manualmente."}
                  </td>
                </tr>
              ) : (
                shown.map((m) => (
                  <tr
                    key={m.id}
                    className="border-b border-border last:border-b-0"
                    style={{ verticalAlign: "top" }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatDateShort(m.movementDate)}
                    </td>
                    <td className="px-4 py-3 break-words">
                      <span>{m.description}</span>
                      {m.isCategorizedManually && (
                        <span className="flex gap-1 flex-wrap mt-1">
                          <span
                            className="inline-flex items-center px-2 py-0.5 bg-muted text-secondary-text"
                            style={{
                              fontSize: "var(--text-xs)",
                              borderRadius: "var(--radius)",
                              fontWeight: "var(--font-weight-medium)",
                            }}
                            title="Categoria corrigida manualmente (não é sobrescrita ao recategorizar)"
                          >
                            Manual
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span
                        style={{
                          fontWeight: "var(--font-weight-medium)",
                          fontVariantNumeric: "tabular-nums",
                          ...(m.amount < 0 ? {} : { color: SUCCESS_COLOR }),
                        }}
                        className={m.amount < 0 ? "text-destructive" : undefined}
                      >
                        {m.amount > 0 ? "+" : ""}
                        {formatCurrency(m.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <CategoryBadge code={m.categoryCode} name={m.categoryName} />
                    </td>
                    <td className="px-4 py-3 break-words">
                      {m.amount > 0 ? (
                        /* Entrada: a entidade é o cliente que pagou. Associa-se à mão, e a
                           reconciliação passa a puxar as faturas emitidas desse cliente. */
                        m.clientId != null ? (
                          <div className="flex flex-col gap-1 items-start">
                            <button
                              type="button"
                              className="text-foreground hover:underline text-left cursor-pointer"
                              onClick={() => openClient(m)}
                            >
                              {m.clientName}
                            </button>
                            <Tooltip
                              text={
                                m.isClientMatchedManually
                                  ? "Escolhido por si."
                                  : "Associado sozinho: o descritivo é o de uma entrada que já associou a este cliente."
                              }
                            >
                              <span
                                className="text-secondary-text"
                                style={{ fontSize: "var(--text-xs)" }}
                              >
                                {m.isClientMatchedManually ? "Cliente · Manual" : "Cliente · Aprendido"}
                              </span>
                            </Tooltip>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-border text-secondary-text hover:bg-accent/30 cursor-pointer"
                            style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                            onClick={() => openClient(m)}
                          >
                            + Associar cliente
                          </button>
                        )
                      ) : m.supplierId != null ? (
                        <div className="flex flex-col gap-1 items-start">
                          {/* Já resolvido: é dado, não pedido de ação — texto normal, como o
                              badge da coluna Categoria. Continua clicável para alterar, mas deixa
                              de competir por atenção com as linhas que precisam mesmo de alguém. */}
                          <Tooltip text={supplierTooltip(m)}>
                            <button
                              type="button"
                              className="text-foreground hover:underline text-left cursor-pointer"
                              onClick={() => openSupplier(m)}
                            >
                              {m.supplierName}
                            </button>
                          </Tooltip>
                          <SupplierSourceBadge
                            source={m.supplierMatchSource}
                            score={m.supplierMatchScore}
                            tooltip={supplierTooltip(m)}
                          />
                        </div>
                      ) : m.suggestedSupplierId != null ? (
                        /* Proposta por confirmar: o nome não é dado como certo — aparece como
                           pergunta, com um clique para aceitar e outro para corrigir. */
                        <div className="flex flex-col gap-1 items-start">
                          <span className="text-secondary-text" style={{ fontSize: "var(--text-xs)" }}>
                            Será
                          </span>
                          <Tooltip text={supplierTooltip(m)}>
                            <span
                              className="cursor-help"
                              style={{ fontWeight: "var(--font-weight-medium)" }}
                            >
                              {m.suggestedSupplierName}?
                            </span>
                          </Tooltip>
                          <div className="flex gap-2 items-center flex-wrap">
                            <button
                              type="button"
                              className="inline-flex items-center px-2 py-1 bg-primary text-primary-foreground disabled:opacity-50 cursor-pointer"
                              style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                              disabled={isSavingSupplier}
                              onClick={() => saveSupplier(m, m.suggestedSupplierId)}
                            >
                              Confirmar
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center px-2 py-1 border border-border text-secondary-text hover:bg-accent/30 cursor-pointer"
                              style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                              onClick={() => openSupplier(m)}
                            >
                              Outro
                            </button>
                            <SupplierSourceBadge
                              source={m.supplierMatchSource}
                              score={m.supplierMatchScore}
                              tooltip={supplierTooltip(m)}
                            />
                          </div>
                        </div>
                      ) : m.supplierNameHint ? (
                        /* O descritivo nomeia um fornecedor que não existe. Em vez de ficar
                           por associar sem explicação, diz-se o nome e cria-se ali mesmo. */
                        <div className="flex flex-col gap-1 items-start">
                          <Tooltip text={supplierTooltip(m)}>
                            <span
                              className="cursor-help text-secondary-text"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              Não registado
                            </span>
                          </Tooltip>
                          <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                            {m.supplierNameHint}
                          </span>
                          <div className="flex gap-2 items-center flex-wrap">
                            <button
                              type="button"
                              className="inline-flex items-center px-2 py-1 bg-primary text-primary-foreground disabled:opacity-50 cursor-pointer"
                              style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                              disabled={isSavingSupplier}
                              onClick={() => createSupplierFromHint(m)}
                            >
                              Criar fornecedor
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center px-2 py-1 border border-border text-secondary-text hover:bg-accent/30 cursor-pointer"
                              style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                              onClick={() => openSupplier(m)}
                            >
                              Associar a existente
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Botão com contorno e cor secundária: como link laranja lia-se como
                           mais um nome de fornecedor no meio da coluna, não como uma ação. */
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 border border-border text-secondary-text hover:bg-accent/30 cursor-pointer"
                          style={{ fontSize: "var(--text-xs)", borderRadius: "var(--radius-button)" }}
                          onClick={() => openSupplier(m)}
                        >
                          + Associar
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 break-words">
                      {m.accountLabel || <span className="text-secondary-text">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {/* Sem quebra de linha: com a coluna à justa, "Apagar" caía sozinho para
                          baixo e as três ações ficavam desalinhadas de linha para linha. */}
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() => openCategorize(m)}
                        >
                          Categorizar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totais do conjunto filtrado e navegação entre páginas */}
        {pageInfo.totalCount > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-secondary-text text-sm">
              Entradas{" "}
              <span style={{ color: SUCCESS_COLOR }}>
                {formatCurrency(pageInfo.totalInflow)}
              </span>
              {"  ·  "}
              Saídas {formatCurrency(pageInfo.totalOutflow)}
              {"  ·  "}
              Saldo {formatCurrency(pageInfo.totalInflow + pageInfo.totalOutflow)}
            </span>

            {pageInfo.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1 border border-border disabled:opacity-50 cursor-pointer"
                  style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-xs)" }}
                  disabled={pageNumber <= 1 || isLoading}
                  onClick={() => setPageNumber((page) => Math.max(1, page - 1))}
                >
                  Anterior
                </button>
                <span aria-live="polite" className="text-secondary-text text-sm">
                  Página {pageNumber} de {pageInfo.totalPages}
                </span>
                <button
                  type="button"
                  className="px-3 py-1 border border-border disabled:opacity-50 cursor-pointer"
                  style={{ borderRadius: "var(--radius-button)", fontSize: "var(--text-xs)" }}
                  disabled={pageNumber >= pageInfo.totalPages || isLoading}
                  onClick={() =>
                    setPageNumber((page) => Math.min(pageInfo.totalPages, page + 1))
                  }
                >
                  Seguinte
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recategorizar por movimento */}
      {categorizeTarget && (
        <Modal
          title="Categorizar movimento"
          onClose={() => setCategorizeTarget(null)}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setCategorizeTarget(null)}
                className="px-4 py-2 min-h-[44px] border border-border"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isCategorizing}
                onClick={handleCategorize}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isCategorizing ? "A guardar…" : "Guardar categoria"}
              </button>
            </>
          }
        >
          {categorizeError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {categorizeError}
            </div>
          )}
          <p className="text-sm text-secondary-text break-words">
            {categorizeTarget.description} · {formatCurrency(categorizeTarget.amount)}
          </p>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Categoria *
            <select
              value={categorizeChoice}
              onChange={(event) => setCategorizeChoice(event.target.value)}
              className={inputClass}
            >
              <option value="">Selecione uma categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-secondary-text">
            A correção manual não é sobrescrita ao recategorizar.
          </p>
        </Modal>
      )}

      {/* Fornecedor */}
      {supplierTarget && (
        <Modal
          title="Entidade do movimento"
          onClose={() => setSupplierTarget(null)}
          footer={
            /* Desassociar à esquerda, sair e guardar à direita. Os três em fila justificados à
               direita não cabiam na largura do modal e o principal caía sozinho para baixo. */
            <div
              className="flex items-center justify-between gap-2 flex-wrap"
              style={{ width: "100%" }}
            >
              <div>
                {supplierTarget.supplierId != null && (
                  <button
                    type="button"
                    disabled={isSavingSupplier}
                    onClick={() => saveSupplier(supplierTarget, null)}
                    className="px-4 py-2 min-h-[44px] border border-border text-destructive cursor-pointer disabled:opacity-50"
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    Desassociar
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSupplierTarget(null)}
                  className="px-4 py-2 min-h-[44px] border border-border"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isSavingSupplier || supplierChoice === ""}
                  onClick={() => saveSupplier(supplierTarget, Number(supplierChoice))}
                  className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  {isSavingSupplier ? "A guardar…" : "Guardar fornecedor"}
                </button>
              </div>
            </div>
          }
        >
          {supplierError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {supplierError}
            </div>
          )}
          <p className="text-sm text-secondary-text break-words">
            {supplierTarget.description} · {formatCurrency(supplierTarget.amount)}
          </p>
          {supplierTarget.supplierMatchReason && (
            <p className="text-xs text-secondary-text break-words">
              Proposta: {supplierTarget.supplierMatchReason}
            </p>
          )}
          <SearchableSelect<string>
            label="Fornecedor"
            required
            labelClassName="text-sm text-secondary-text"
            labelStyle={{}}
            panelPosition="fixed"
            value={supplierChoice === "" ? null : supplierChoice}
            onChange={(next) => setSupplierChoice(next ?? "")}
            options={supplierSelectOptions}
            loading={suppliersLoading}
            placeholder="Selecione um fornecedor"
            searchPlaceholder="Procurar fornecedor…"
          />
          <p className="text-xs text-secondary-text">
            Guardar ensina o sistema: os próximos movimentos com este descritivo ficam associados
            sozinhos, e a sua escolha nunca é substituída por um novo reconhecimento.
          </p>

          {/* Criar ali mesmo. O descritivo do banco nomeia muita gente que não está registada, e
              mandar o utilizador à área de Fornecedores fazia-o perder o movimento de vista. */}
          <div className="border-t border-border pt-3 space-y-2">
            <label
              htmlFor="novo-fornecedor-movimento"
              className="block text-sm text-secondary-text"
            >
              Não está na lista? Escreve o nome e regista
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                id="novo-fornecedor-movimento"
                type="text"
                value={newSupplierName}
                onChange={(event) => setNewSupplierName(event.target.value)}
                placeholder="Nome do fornecedor"
                className={inputClass}
                style={{ flexGrow: 1, minWidth: 180 }}
              />
              <button
                type="button"
                disabled={isSavingSupplier || newSupplierName.trim() === ""}
                onClick={() => createSupplierWithName(supplierTarget, newSupplierName)}
                className="px-4 py-2 min-h-[44px] border border-border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Criar e associar
              </button>
            </div>
            <p className="text-xs text-secondary-text">
              Se já existir um fornecedor com esse nome, o movimento fica associado a esse em vez de
              se criar outro igual.
            </p>
          </div>
        </Modal>
      )}

      {/* Cliente da entrada */}
      {clientTarget && (
        <Modal
          title="Entidade do movimento"
          onClose={() => setClientTarget(null)}
          footer={
            <div
              className="flex items-center justify-between gap-2 flex-wrap"
              style={{ width: "100%" }}
            >
              <div>
                {clientTarget.clientId != null && (
                  <button
                    type="button"
                    disabled={isSavingClient}
                    onClick={() => saveClient(clientTarget, null)}
                    className="px-4 py-2 min-h-[44px] border border-border text-destructive cursor-pointer disabled:opacity-50"
                    style={{ borderRadius: "var(--radius-button)" }}
                  >
                    Desassociar
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setClientTarget(null)}
                  className="px-4 py-2 min-h-[44px] border border-border"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isSavingClient || clientChoice === ""}
                  onClick={() => saveClient(clientTarget, Number(clientChoice))}
                  className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  {isSavingClient ? "A guardar…" : "Guardar cliente"}
                </button>
              </div>
            </div>
          }
        >
          {clientError && (
            <div
              className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
              style={{ borderRadius: "var(--radius)" }}
              role="alert"
            >
              {clientError}
            </div>
          )}
          <p className="text-sm text-secondary-text break-words">
            {clientTarget.description} · {formatCurrency(clientTarget.amount)}
          </p>
          <SearchableSelect<string>
            label="Cliente"
            required
            labelClassName="text-sm text-secondary-text"
            labelStyle={{}}
            panelPosition="fixed"
            value={clientChoice === "" ? null : clientChoice}
            onChange={(next) => setClientChoice(next ?? "")}
            options={clientSelectOptions}
            loading={clientsLoading}
            placeholder="Selecione um cliente"
            searchPlaceholder="Procurar cliente…"
          />
          <p className="text-xs text-secondary-text">
            Numa entrada a entidade é quem pagou. Depois de associado, a Reconciliação passa a
            propor primeiro as faturas emitidas a este cliente.
          </p>

          <div className="border-t border-border pt-3 space-y-2">
            <label
              htmlFor="novo-cliente-movimento"
              className="block text-sm text-secondary-text"
            >
              Não está na lista? Escreve o nome e regista
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                id="novo-cliente-movimento"
                type="text"
                value={newClientName}
                onChange={(event) => setNewClientName(event.target.value)}
                placeholder="Nome do cliente"
                className={inputClass}
                style={{ flexGrow: 1, minWidth: 180 }}
              />
              <button
                type="button"
                disabled={isSavingClient || newClientName.trim() === ""}
                onClick={() => createClientWithName(clientTarget, newClientName)}
                className="px-4 py-2 min-h-[44px] border border-border cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Criar e associar
              </button>
            </div>
            <p className="text-xs text-secondary-text">
              Se já existir um cliente com esse nome, o movimento fica associado a esse em vez de
              se criar outro igual.
            </p>
          </div>
        </Modal>
      )}

    </div>
  );
}
